import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import { SITE_URL, DEFAULT_OG_IMAGE } from "./config/seo-data";

import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import AdminRegister from "./pages/AdminRegister";
import ResetPassword from "./pages/ResetPassword";
import ProtectedRoute from "./components/ProtectedRoute";
import MapsProvider from "./components/MapsProvider";
import ErrorDisabler from "./components/admin/ErrorDisabler";
import BookingErrorBoundary from "./components/BookingErrorBoundary";
import "./components/MainPageErrorHandler"; // Auto-initializes without import name
import { initializeSecurity, sessionManager } from "./utils/security";

// Optimized component imports
import Navigation from './components/Navigation';
import CookieConsent from './components/CookieConsent';
import HeroSection from './components/HeroSection';
import AboutUs from './components/AboutUs';
import Services from './components/Services';
import DestinationsSection from './components/DestinationsSection';
import TestimonialsSection from './components/TestimonialsSection';
import Contact from './components/Contact';
import Footer from './components/Footer';
import StandaloneBooking from './pages/StandaloneBooking';

// SEO Landing Pages
import AirportTransfers from './pages/AirportTransfers';
import PortTransfers from './pages/PortTransfers';
import PrivateTours from './pages/PrivateTours';
import HourlyChauffeur from './pages/HourlyChauffeur';
import WeddingTransfers from './pages/WeddingTransfers';
import GroupTransfers from './pages/GroupTransfers';
import FAQ from './pages/FAQ';
import DestinationTransfer from './pages/DestinationTransfer';
import TourPage from './pages/TourPage';
import BlogPost from './pages/BlogPost';
import NorthCorfuTransfers from './pages/NorthCorfuTransfers';

import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function BookingPage() {
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    const securityInit = initializeSecurity();
    if (securityInit.securityCheck.isSecure) {
      const existingSession = sessionManager.getSession();
      void existingSession;
    }
  }, []);

  const handleDestinationBooking = useCallback((destination) => {
    const element = document.getElementById('booking');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.dispatchEvent(new CustomEvent('setDestination', {
        detail: {
          name: destination.name,
          position: destination.position,
          lat: destination.position.lat,
          lng: destination.position.lng
        }
      }));
    }
  }, []);

  // Preload critical images — single state update when all done
  useEffect(() => {
    const criticalImages = [
      '/images/backgrounds/cfu_corfu_old_town.webp',
      '/images/backgrounds/Visit-Kassiopi-Homepage-Hero.webp',
      '/images/backgrounds/corfu-background-3.webp',
      '/images/backgrounds/HD-wallpaper-corfu-island-summer-bay-beautiful-city-greece-white-yachts.webp',
    ];

    let loadedCount = 0;
    const onLoad = () => {
      loadedCount++;
      if (loadedCount >= criticalImages.length) {
        setImagesLoaded(true);
      }
    };

    criticalImages.forEach((src) => {
      const img = new Image();
      img.onload = onLoad;
      img.onerror = onLoad;
      img.src = src;
    });

    const timeout = setTimeout(() => setImagesLoaded(true), 2500);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <Helmet>
        <title>Lasmari Chauffeurs | Corfu Transfers & Airport Transfer</title>
        <meta name="description" content="Lasmari Chauffeurs: private Corfu airport transfers & chauffeur service. Mercedes-Benz fleet, meet & greet, flight monitoring, 24/7 to Kassiopi, Sidari, Paleokastritsa & all destinations." />
        <link rel="canonical" href={`${SITE_URL}/`} />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      
      <div className="min-h-screen bg-white text-[#1a1012] font-sans overflow-x-hidden">
        {/* Navigation */}
        <Navigation />
      
      {/* Hero Section */}
      <HeroSection imagesLoaded={imagesLoaded} />

      {/* About Us Section */}
      <AboutUs imagesLoaded={imagesLoaded} />

      {/* Services Section */}
      <Services imagesLoaded={imagesLoaded} />

      {/* Destinations Section */}
      <DestinationsSection 
        imagesLoaded={imagesLoaded} 
        handleDestinationBooking={handleDestinationBooking}
      />

      {/* Contact Section */}
      <Contact />

      {/* Testimonials Section */}
      <TestimonialsSection imagesLoaded={imagesLoaded} />

      {/* Book Now CTA */}
      <section id="booking" className="relative py-24 lg:py-32 overflow-hidden" style={{ backgroundColor: '#ba1609' }}>
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/80 text-xs uppercase tracking-[0.4em] mb-4 font-light">Ready to Go?</p>
          <h2 className="text-3xl lg:text-5xl font-light text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Book Your <span className="italic">Transfer</span>
          </h2>
          <p className="text-white/85 text-sm lg:text-base font-light leading-relaxed mb-10 max-w-xl mx-auto">
            Choose your destination, pick a date, and we'll handle the rest. Fixed prices, no hidden fees, and 24/7 availability.
          </p>
          <Link
            to="/booking"
            className="inline-block px-10 py-4 bg-white text-[#ba1609] rounded-full font-medium tracking-wide hover:bg-white/90 transition-all duration-300 shadow-xl uppercase text-sm"
          >
            Book Now
          </Link>
        </div>
      </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <MapsProvider>
        <ErrorDisabler />
        <Router>
        <ScrollToTop />
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#ffffff',
              color: '#1a1012',
              border: '1px solid rgba(186, 22, 9, 0.25)',
            },
            success: {
              iconTheme: {
                primary: '#ba1609',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ba1609',
                secondary: '#fff',
              },
            },
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            <BookingErrorBoundary>
              <BookingPage />
            </BookingErrorBoundary>
          } />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin-register" element={<AdminRegister />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/booking" element={<StandaloneBooking />} />

          {/* SEO Landing Pages */}
          <Route path="/airport-transfers" element={<AirportTransfers />} />
          <Route path="/port-transfers" element={<PortTransfers />} />
          <Route path="/private-tours" element={<PrivateTours />} />
          <Route path="/hourly-chauffeur" element={<HourlyChauffeur />} />
          <Route path="/wedding-transfers" element={<WeddingTransfers />} />
          <Route path="/group-transfers" element={<GroupTransfers />} />
          <Route path="/north-corfu-transfers" element={<NorthCorfuTransfers />} />
          <Route path="/faq" element={<FAQ />} />

          {/* Dynamic Destination Pages */}
          <Route path="/transfers/:slug" element={<DestinationTransfer />} />

          {/* Dynamic Tour Pages */}
          <Route path="/tours/:slug" element={<TourPage />} />

          {/* Blog/Guide Pages */}
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Admin Routes - Protected routes requiring authentication */}
          <Route path="/admin-dashboard/*" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

          {/* Redirect unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieConsent />
        </Router>
      </MapsProvider>
    </HelmetProvider>
  );
}